import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { Button, Form, FormGroup, Spinner } from 'reactstrap';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import intl from 'react-intl-universal';

import Icon from 'components/Icon';
import Page from 'components/Page';
import Checkbox from 'components/Checkbox';
import PageHeader from 'components/PageHeader';
import PageFooter from 'components/PageFooter';
import {
  closeForm,
  postAppointmentForm,
  setCurrentFormStep,
  togglePrivacyPolicyModal,
  toggleTermsOfServiceModal,
} from 'modules/forms/actions/form.actions';
import { useTypedSelector } from 'hooks/useTypedSelector';
import { signOutPatient } from 'modules/auth/actions/auth.actions';
import { EAppointmentFormStatus, IAppointmentForm } from '../interfaces/form.interfaces';
import PrivacyPolicyModal from '../components/PrivacyPolicyModal';
import TermsOfServiceModal from '../components/TermsOfServiceModal';
import { getFormResultHTML } from '../helpers/formResult.helper';
import FormResult from './FormResult';

const FormSubmit = (): JSX.Element => {
  const history = useHistory();
  const dispatch = useDispatch();
  const currentForm = useTypedSelector(state => state.form.currentForm);
  const currentStep = useTypedSelector(state => state.form.currentStep);
  const appointment = useTypedSelector(state => state.form.appointment);
  const stateForm = useTypedSelector(state => state.form.form);
  const guest = useTypedSelector(state => state.auth.guest);
  const location = useTypedSelector(state => state.app.location);
  const postLoading = useTypedSelector(state => state.form.loading.postForm);

  const methods = useForm<
    Partial<IAppointmentForm> & {
      patientConsent: boolean;
      healthRecordAccessConsent: boolean;
    }
  >({
    defaultValues: {
      agreedTerms: false,
      agreedToReceiveMessagesFromPharmacy: false,
      patientConsent: false,
      healthRecordAccessConsent: false,
    },
    mode: 'onSubmit',
  });
  const { control, handleSubmit, watch } = methods;

  const isSubmitDisabled =
    (!!guest && !watch('agreedTerms')) ||
    (!!currentForm?.showPatientConsent && !watch('patientConsent')) ||
    (!!currentForm?.showHealthRecordAccessConsent && !watch('healthRecordAccessConsent'));

  const onBack = () => {
    history.goBack();
    dispatch(setCurrentFormStep(currentStep - 1));
  };

  const blobToBase64 = (blob: Blob | undefined): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!blob) {
        resolve('');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          reject(new Error('Failed to convert blob to base64'));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const onSubmit = async (values: Partial<IAppointmentForm>) => {
    let signatureBase64 = '';
    const signatureName = currentForm?.signature?.signatureName;

    if (currentForm?.signature) {
      try {
        signatureBase64 = await blobToBase64(currentForm?.signature?.signatureBlob as Blob);
      } catch (error) {
        console.error(error);
      }
    }

    dispatch(
      postAppointmentForm({
        form: {
          appointmentUuid: appointment?.uuid,
          status: stateForm.status || EAppointmentFormStatus.COMPLETED,
          agreedTerms: true,
          agreedToReceiveMessagesFromPharmacy: values.agreedToReceiveMessagesFromPharmacy,
          response: getFormResultHTML(
            { ...currentForm },
            FormResult,
            signatureBase64,
            signatureName,
          ),
        },
      }),
    );
  };

  const onClose = () => {
    dispatch(closeForm());
  };

  const onFinish = () => {
    dispatch(signOutPatient());
  };

  return (
    <Page>
      <PageHeader step={currentStep + 1} maxSteps={currentForm?.sections?.length} />
      <FormProvider {...methods}>
        <Form className="page" onSubmit={handleSubmit(onSubmit)}>
          <div className="page-content">
            <main id="page-main-scrollable" className="page-main">
              <h3>Thank you for taking the time to complete this form.</h3>
              {!!stateForm.uuid && <div>Your responses have been submitted.</div>}
              {!stateForm.uuid && (!!guest || !!currentForm?.showPatientConsent) && (
                <div className="form-questions form-submit">
                  {!!guest && (
                    <Controller
                      name="agreedTerms"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <FormGroup className="mb-md">
                          <Checkbox
                            id="agreedTerms"
                            name="agreedTerms"
                            checked={value}
                            label={
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <div>*</div>
                                <div>
                                  <div style={{ marginBottom: '4px', fontWeight: 'bold' }}>
                                    Terms & Privacy
                                  </div>
                                  <span>
                                    I have read and agree to the{' '}
                                    <span
                                      className="form-submit__link"
                                      role="presentation"
                                      onClick={e => {
                                        e.preventDefault();
                                        dispatch(toggleTermsOfServiceModal(true));
                                      }}
                                    >
                                      Terms of Services
                                    </span>{' '}
                                    and{' '}
                                    <span
                                      className="form-submit__link"
                                      role="presentation"
                                      onClick={e => {
                                        e.preventDefault();
                                        dispatch(togglePrivacyPolicyModal(true));
                                      }}
                                    >
                                      Privacy Policy
                                    </span>
                                  </span>
                                </div>
                              </div>
                            }
                            onChange={e => onChange(e.target.checked)}
                          />
                        </FormGroup>
                      )}
                    />
                  )}
                  {!!currentForm?.showPatientConsent && (
                    <>
                      <Controller
                        name="patientConsent"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <FormGroup className="mb-md" style={{ maxWidth: '900px' }}>
                            <Checkbox
                              id="patientConsent"
                              name="patientConsent"
                              checked={value}
                              label="* I confirm that the information I have provided is accurate and complete to the best of my knowledge. I consent to receive treatment and care from the pharmacy team, including the administration of any medications or services deemed clinically appropriate."
                              onChange={e => onChange(e.target.checked)}
                            />
                          </FormGroup>
                        )}
                      />
                    </>
                  )}
                  {!!currentForm?.showHealthRecordAccessConsent && (
                    <>
                      <Controller
                        name="healthRecordAccessConsent"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <FormGroup className="mb-md" style={{ maxWidth: '900px' }}>
                            <Checkbox
                              id="healthRecordAccessConsent"
                              name="healthRecordAccessConsent"
                              checked={value}
                              label={
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <div>*</div>
                                  <div>
                                    <div style={{ marginBottom: '4px', fontWeight: 'bold' }}>
                                      Access to My Health Record
                                    </div>
                                    <div>
                                      I consent to the practitioner accessing my summary health
                                      record (a secure electronic record containing key information
                                      such as medicines, allergies and past treatments) for the
                                      purpose of this consultation.
                                    </div>
                                  </div>
                                </div>
                              }
                              onChange={e => onChange(e.target.checked)}
                            />
                          </FormGroup>
                        )}
                      />
                    </>
                  )}
                  {!!guest && (
                    <Controller
                      name="agreedToReceiveMessagesFromPharmacy"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <FormGroup>
                          <Checkbox
                            id="agreedToReceiveMessagesFromPharmacy"
                            name="agreedToReceiveMessagesFromPharmacy"
                            checked={value}
                            label="I would like to receive updates and additional information from the pharmacy"
                            onChange={e => onChange(e.target.checked)}
                          />
                        </FormGroup>
                      )}
                    />
                  )}
                </div>
              )}
            </main>

            <PageFooter>
              {!stateForm.uuid && (
                <Button
                  color="primary"
                  outline
                  type="button"
                  block
                  disabled={postLoading}
                  onClick={onBack}
                >
                  <Icon iconName="arrow-left" />
                  {intl.get('button.back')}
                </Button>
              )}
              {(!!guest || !!currentForm?.showPatientConsent) && !stateForm.uuid && (
                <Button
                  color="primary"
                  type="button"
                  disabled={postLoading || isSubmitDisabled}
                  block
                  onClick={handleSubmit(onSubmit)}
                >
                  {postLoading && <Spinner size="sm" />}
                  {intl.get('button.submit')}
                </Button>
              )}
              {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                !guest && !!window.ReactNativeWebView && !location && !!stateForm.uuid && (
                  <Button color="primary" type="button" block onClick={onClose}>
                    {intl.get('button.close')}
                  </Button>
                )
              }
              {!!location && !!stateForm.uuid && (
                <Button color="primary" type="button" block onClick={onFinish}>
                  {intl.get('forms.nextPatient')}
                </Button>
              )}
            </PageFooter>
          </div>
        </Form>
      </FormProvider>

      <TermsOfServiceModal />
      <PrivacyPolicyModal />
    </Page>
  );
};

export default FormSubmit;
